import React, { useRef, useCallback, useEffect } from "react"
import Item from "./allowed-items-item"

const Items = ({
  query,
  allItems,
  hash,
  setHash,
  mopinionScript,
  handLuggageLabel,
  checkinLabel,
  scrollIntoView,
  amountShown,
  activateAutoScroll,
  increaseAmount,
  initialAmount,
  handleMoreClick,
  handleHideClick,
  isMobile,
  shownItems,
  searchBarActive,
  alphabeticalHeadline,
  numberOfItems,
  numberOfItemsIncrement,
  numberOfItemsIncrementMobile,
  showLessItemsText,
  showMoreItemsText,
  noResultsText,
  noResultsHeader,
  noResultsSubHeader,
  ariaLabelAllowed,
  ariaLabelSometimesAllowed,
  ariaLabelNotAllowed,
}) => {
  const observer = useRef(null)
  const prevCountRef = useRef(amountShown)
  const keyboardTriggeredRef = useRef(false)

  // Intersection observer for infinite scroll
  const lastItemElement = useCallback(
    (node) => {
      if (!node || allItems.length === 0) return
      if (observer.current) observer.current.disconnect()
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && activateAutoScroll && shownItems.length !== allItems.length) {
          handleMoreClick(isMobile ? numberOfItemsIncrementMobile : numberOfItemsIncrement)
        }
      })
      observer.current.observe(node)
    },
    [amountShown, shownItems, activateAutoScroll],
  )

  // Focus first newly shown item after keyboard expansion
  useEffect(() => {
    if (keyboardTriggeredRef.current) {
      const prev = prevCountRef.current
      requestAnimationFrame(() => {
        // Scope to #allowed-items to avoid duplicates
        const list = document.querySelectorAll("#allowed-items .item-list-item .itemtable")
        if (list[prev]) list[prev].focus()
      })
      keyboardTriggeredRef.current = false
    }
    prevCountRef.current = shownItems.length
  }, [shownItems.length])

  // Handler for expanding/hiding via click or keyboard
  const handleExpander = (e) => {
    // Detect keyboard invocation: detail===0 or keydown
    const isKeyboard = (e.nativeEvent && e.nativeEvent.detail === 0) || ["keydown", "keyup"].includes(e.type)
    if (isKeyboard) {
      keyboardTriggeredRef.current = true
      prevCountRef.current = amountShown
    }

    if (allItems.length === shownItems.length) {
      prevCountRef.current = 0
      handleHideClick()
    } else {
      handleMoreClick(isMobile ? numberOfItemsIncrementMobile : numberOfItemsIncrement)
    }
  }

  const itemsToRender = query.length >= 2 ? shownItems : allItems

  if (!itemsToRender || itemsToRender.length === 0) {
    return (
      <div className="allowed-item-list__no-results">
        <div className="section-header section-header--small">
          <p>{noResultsText.replace("{input}", query)}</p>
          <div>
            <h2>{noResultsHeader || "Ingen resultater"}</h2>
            <p>{noResultsSubHeader || "Der er desværre ingen resultater der matcher din søgning."}</p>
          </div>
        </div>
      </div>
    )
  }

  // Render items and attach intersection ref on last old item
  const mappedItems = itemsToRender.map((item, index) => {
    const lastOldIndex = prevCountRef.current - 1
    const isLastOld = index === lastOldIndex
    return (
      <Item
        key={`${item.category}-${index}`}
        item={item}
        hash={hash}
        setHash={setHash}
        mopinionScript={mopinionScript}
        handLuggageLabel={handLuggageLabel}
        checkinLabel={checkinLabel}
        scrollIntoView={scrollIntoView}
        amountShown={amountShown}
        itemNumber={index + 1}
        refProp={isLastOld ? lastItemElement : null}
        isLastItem={isLastOld}
        ariaLabelAllowed={ariaLabelAllowed}
        ariaLabelSometimesAllowed={ariaLabelSometimesAllowed}
        ariaLabelNotAllowed={ariaLabelNotAllowed}
      />
    )
  })

  // Split old vs new items
  let adjustedInc = increaseAmount
  const diff = itemsToRender.length - amountShown
  if (diff < 0) adjustedInc = increaseAmount - Math.abs(diff)
  const splitIndex = mappedItems.length - adjustedInc
  const oldItems = mappedItems.slice(0, splitIndex)
  const newItems = mappedItems.slice(splitIndex)

  return (
    <>
      {!searchBarActive && <h2 className="items-headline">{alphabeticalHeadline || "Fra A-Z"}</h2>}
      <ul>{oldItems}</ul>
      <div className={mappedItems.length <= initialAmount ? "" : "next-items-wrapper"}>{newItems}</div>
      <div className={`buttoncontainer ${itemsToRender.length > numberOfItems ? "has-button" : ""}`}>
        {allItems.length >= shownItems.length && query.length < 2 && (
          <button className="button button--expander" onClick={handleExpander} onKeyDown={handleExpander}>
            <span className="text">
              {allItems.length === shownItems.length ? showLessItemsText || "Vis færre" : showMoreItemsText || "Vis flere"}
            </span>
          </button>
        )}
      </div>
    </>
  )
}

export default Items
